import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import { z } from 'zod';
import { useApi } from '../../providers/api';
import type {
  LabelRevisionData,
  PrintFormState,
  PrintJobRequest,
  PrintJobResult,
} from '../../types/types';
import { fetchAllPages } from '../../utils/pagination';
import { getResponseErrorMessage } from '../../utils/response';
import { createLabelValidationSchema, extractValidationErrors } from './schemas';

export const sortRevisions = (revisions: string[]): string[] => {
  return [...revisions].sort((a, b) => {
    const aParts = a.split('.').map((num) => parseInt(num, 10));
    const bParts = b.split('.').map((num) => parseInt(num, 10));

    const maxLength = Math.max(aParts.length, bParts.length);

    for (let i = 0; i < maxLength; i++) {
      const aPart = aParts[i] || 0;
      const bPart = bParts[i] || 0;

      if (aPart !== bPart) {
        return bPart - aPart;
      }
    }

    return 0;
  });
};

export const usePrintData = () => {
  const { client } = useApi();

  const {
    data: printers,
    isLoading: printersLoading,
    error: printersError,
  } = useSWR('printers', () =>
    fetchAllPages((query) => client.v1.printers.$get({ query }).then((r) => r.json()))
  );

  const {
    data: labels,
    isLoading: labelsLoading,
    error: labelsError,
  } = useSWR('labels', () =>
    fetchAllPages((query) => client.v1.labels.$get({ query }).then((r) => r.json()))
  );

  const getLabelRevision = useCallback(
    async (labelId: number, revision?: string) => {
      return await (
        await client.v1.labels[':id'].$get({
          param: { id: labelId.toString() },
          query: { revision: revision },
        })
      ).json();
    },
    [client]
  );

  const submitPrintJob = useCallback(
    async (data: PrintJobRequest): Promise<PrintJobResult & { accepted?: boolean }> => {
      const response = await client.v2['print-jobs'].$post({ json: data });

      if (!response.ok) {
        const message = await getResponseErrorMessage(
          response,
          `Request failed with status ${response.status}`
        );
        throw new Error(message);
      }

      const result = await response.json();
      return { ...result, accepted: response.status === 202 };
    },
    [client]
  );

  return {
    printers,
    labels,
    printersLoading,
    labelsLoading,
    printersError,
    labelsError,
    getLabelRevision,
    submitPrintJob,
  };
};

export const useFormState = () => {
  const [formState, setFormState] = useState<PrintFormState>({
    selectedPrinter: null,
    selectedLabel: null,
    selectedRevision: '',
    variables: [],
    validationErrors: {},
    isPrinting: false,
  });

  const updateFormState = useCallback((updates: Partial<PrintFormState>) => {
    setFormState((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetForm = useCallback(() => {
    setFormState({
      selectedPrinter: null,
      selectedLabel: null,
      selectedRevision: '',
      variables: [],
      validationErrors: {},
      isPrinting: false,
    });
  }, []);

  return {
    formState,
    updateFormState,
    resetForm,
  };
};

export const useLabelRevision = (
  selectedLabel: number | null,
  selectedRevision: string,
  getLabelRevision: (
    labelId: number,
    revision?: string
  ) => Promise<{ revision: LabelRevisionData | null }>
) => {
  const { data } = useSWR(
    selectedLabel ? (['label-revision', selectedLabel, selectedRevision] as const) : null,
    async ([, labelId, revision]) => {
      const response = await getLabelRevision(labelId, revision || undefined);
      return response.revision ?? null;
    }
  );

  return data ?? null;
};

export const useValidation = (
  labelRevisionData: LabelRevisionData | null,
  fieldDisplayNames?: ReadonlyMap<string, string>
) => {
  const validationSchema = useMemo(() => {
    return createLabelValidationSchema(labelRevisionData, fieldDisplayNames);
  }, [labelRevisionData, fieldDisplayNames]);

  const validateField = useCallback(
    (
      fieldName: string,
      id: string,
      value: string | number,
      setValidationErrors: (fn: (prev: Record<string, string>) => Record<string, string>) => void
    ) => {
      try {
        const schemaShape = validationSchema.shape as Record<string, z.ZodTypeAny>;
        const fieldSchema = schemaShape[fieldName];
        if (fieldSchema) {
          fieldSchema.parse(value);
          setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[`${fieldName}_${id}`];
            return newErrors;
          });
        }
      } catch (error) {
        if (error instanceof z.ZodError) {
          const errorMessage = error.issues[0]?.message || 'Invalid value';
          setValidationErrors((prev) => ({
            ...prev,
            [`${fieldName}_${id}`]: errorMessage,
          }));
        } else {
          setValidationErrors((prev) => ({
            ...prev,
            [`${fieldName}_${id}`]: 'Invalid value',
          }));
        }
      }
    },
    [validationSchema]
  );

  const validateForm = useCallback(
    (
      variables: Record<string, string | number>[],
      setValidationErrors?: (fn: (prev: Record<string, string>) => Record<string, string>) => void
    ) => {
      try {
        variables.map(({ id, ...rest }) => validationSchema.parse(rest));
        return { isValid: true, errors: {} };
      } catch (error) {
        if (error instanceof z.ZodError) {
          const { fieldErrors, toastMessage } = extractValidationErrors(error, fieldDisplayNames);

          if (setValidationErrors) {
            setValidationErrors((prev) => ({
              ...prev,
              ...fieldErrors,
            }));
          }

          return { isValid: false, errors: fieldErrors, toastMessage };
        }
        return {
          isValid: false,
          errors: {},
          toastMessage: 'Validation failed. Please check your input.',
        };
      }
    },
    [validationSchema, fieldDisplayNames]
  );

  return {
    validateField,
    validateForm,
  };
};
